import { faBolt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Chip, Divider, Paper, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { isEmpty, startCase } from 'lodash';
import Markdown from 'react-markdown';

import CaseDetailCard from '@/components/CaseDetailCard';
import { DATE_TIME_FORMAT } from '@/constants/date.constants';
import { MARKDOWN_REMARK_PLUGINS } from '@/constants/markdown.constants';
import { caseDetailsAtom } from '@/pages/Case/Case.store';
import { getColorByVerdict } from '@/pages/Case/Case.utils';
import { CaseIdLink } from '@/pages/Case/CaseIdLink';

import useStyles from './CaseDetailsAI.styles';
import { InvestigationCycle } from './InvestigationCycle';

export const CaseDetailsAI = () => {
  const [caseDetails] = useAtom(caseDetailsAtom);
  const { classes } = useStyles();

  if (isEmpty(caseDetails?.ai_output)) {
    return (
      <Typography sx={{ p: 2, fontStyle: 'italic' }}>
        No AI analysis available for this case.
      </Typography>
    );
  }

  if (caseDetails?.ai_output?.error) {
    return (
      <Typography sx={{ p: 2, fontStyle: 'italic' }} color="error">
        Error in AI analysis: {caseDetails?.ai_output?.error}
      </Typography>
    );
  }

  return (
    <Stack className={classes.caseDetailsAI}>
      <CaseDetailCard title="Summary">
        <Box className="markdown-body">
          <Markdown
            disallowedElements={['img']}
            remarkPlugins={MARKDOWN_REMARK_PLUGINS}
            unwrapDisallowed
            components={{
              a: CaseIdLink
            }}
          >
            {caseDetails.ai_output.operational_summary || ''}
          </Markdown>
        </Box>
        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Final Verdict:
          </Typography>
          <Chip
            label={startCase(caseDetails?.ai_output?.final_verdict)}
            variant="twoTone"
            color={getColorByVerdict(caseDetails?.ai_verdict || '')}
          />
        </Stack>
      </CaseDetailCard>

      <CaseDetailCard title="Analysis">
        <Box className="markdown-body">
          <Markdown
            disallowedElements={['img']}
            remarkPlugins={MARKDOWN_REMARK_PLUGINS}
            unwrapDisallowed
            components={{
              a: CaseIdLink
            }}
          >
            {caseDetails?.ai_output?.context_analysis || ''}
          </Markdown>
        </Box>
        <Divider style={{ margin: '1rem 0' }} />
        <Box className="markdown-body">
          <Markdown
            disallowedElements={['img']}
            remarkPlugins={MARKDOWN_REMARK_PLUGINS}
            unwrapDisallowed
            components={{
              a: CaseIdLink
            }}
          >
            {caseDetails?.ai_output?.final_reasoning || ''}
          </Markdown>
        </Box>
      </CaseDetailCard>

      <CaseDetailCard title="Research">
        <Typography gutterBottom>
          <CaseIdLink>{caseDetails?.research?.reasoning}</CaseIdLink>
        </Typography>
        <Stack
          sx={{ flexDirection: 'row', mt: 2, alignItems: 'center', gap: 1 }}
        >
          <Typography variant="h6">Initial Verdict:</Typography>
          <Chip
            label={startCase(caseDetails?.ai_output?.initial_verdict)}
            variant="twoTone"
            color={getColorByVerdict(caseDetails?.ai_verdict || '')}
          />
        </Stack>

        <Divider style={{ margin: '1rem 0' }} />

        {caseDetails?.research?.investigationCycles?.map((cycle, index) => (
          <InvestigationCycle
            key={`cycle-${cycle.id}`}
            cycle={cycle}
            index={index}
          />
        ))}

        {caseDetails?.continuedResearch?.map((analysis, index) => (
          <div
            key={`${caseDetails?.id}-continued-analysis-${analysis.analysisGuidance}`}
          >
            <Paper
              className={classes.continuedAnalysisPaper}
              variant="outlined"
            >
              <Box sx={{ pt: 0.25 }}>
                <FontAwesomeIcon
                  icon={faBolt}
                  size="xl"
                  color="var(--mui-palette-primary-main)"
                />
              </Box>
              <Box sx={{ flex: 1, pr: 1 }}>
                <Stack
                  sx={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pb: 1
                  }}
                  spacing={2}
                >
                  <Typography variant="h5" component="h5" gutterBottom>
                    Continued Analysis {index + 1}
                  </Typography>
                  <Box>
                    {dayjs(analysis.analysisStart).format(DATE_TIME_FORMAT)}
                  </Box>
                </Stack>
                <Typography
                  sx={{ fontStyle: 'italic', fontSize: '1rem' }}
                  gutterBottom
                >
                  <CaseIdLink>{analysis.analysisGuidance}</CaseIdLink>
                </Typography>
              </Box>
            </Paper>
            {analysis.investigationCycles.map((cycle, cycleIndex) => (
              <InvestigationCycle
                key={`${caseDetails?.id}-continued-analysis-cycle-${cycle.id}`}
                cycle={cycle}
                index={cycleIndex}
              />
            ))}
          </div>
        ))}
      </CaseDetailCard>
    </Stack>
  );
};
export default CaseDetailsAI;
